import { computed, h } from 'vue';

import { useState } from '@components/composable';
import { warn } from '@components/utils';

import { FilterDropdown } from '../components/Filter';
import { getColumnKey, getColumnPos, renderColumnTitle } from '../utils';

import type { NTable } from '../interface';
import type { Ref } from 'vue';

export interface IFilterOptions<RecordType> {
  mergedColumns: Ref<NTable.NColumn.TColumns<RecordType>>;
  tableLocale: Ref<NTable.ILocale>;
  getPopupContainer?: Ref<NTable.NCustomize.TGetPopupContainer>;
  onFilterChange: (
    filters: Record<string, NTable.TFilterValue | null>,
    filterStates: NTable.IFilterState<RecordType>[]
  ) => void;
}

export interface IUseFilter<RecordType> {
  transformColumns: NTable.NColumn.TTransformColumns<RecordType>;
  mergedFilterStates: Ref<NTable.IFilterState<RecordType>[]>;
  filters: Ref<Record<string, NTable.TFilterValue | null>>;
}

export function useFilter<RecordType>({
  mergedColumns,
  tableLocale,
  getPopupContainer,
  onFilterChange
}: IFilterOptions<RecordType>): IUseFilter<RecordType> {
  const [filterStates, setFilterStates] = useState<NTable.IFilterState<RecordType>[]>(
    collectFilterStates(mergedColumns.value, true)
  );

  const mergedFilterStates = computed(() => {
    const collectedStates = collectFilterStates(mergedColumns.value, false);
    const filteredKeysIsNotControlled = collectedStates.every(({ filteredKeys }) => filteredKeys === undefined);

    if (filteredKeysIsNotControlled) {
      return filterStates.value;
    }

    const filteredKeysIsAllControlled = collectedStates.every(({ filteredKeys }) => filteredKeys !== undefined);

    if (!(filteredKeysIsAllControlled || filteredKeysIsNotControlled)) {
      warn('CdsTable', '`filteredKeys` should be all controlled or not controlled.');
    }

    return collectedStates;
  });

  const filters = computed(() => generateFilterInfo(mergedFilterStates.value));

  const triggerFilter = (filterState: NTable.IFilterState<RecordType>) => {
    const newFilterStates = mergedFilterStates.value.filter(({ key }) => key !== filterState.key);

    newFilterStates.push(filterState);
    setFilterStates(newFilterStates);
    onFilterChange(generateFilterInfo(newFilterStates), newFilterStates);
  };

  const transformColumns = (innerColumns: NTable.NColumn.TColumns<RecordType>) => {
    return injectFilter(
      innerColumns,
      mergedFilterStates.value,
      triggerFilter,
      getPopupContainer?.value,
      tableLocale.value
    );
  };

  return { transformColumns, mergedFilterStates, filters };
}

export function flattenKeys(filters?: NTable.NColumn.IFilterItem[]) {
  let keys: NTable.TFilterValue = [];

  (filters || []).forEach(({ value, children }) => {
    keys.push(value);

    if (children) {
      keys = [...keys, ...flattenKeys(children)];
    }
  });

  return keys;
}

export function getFilterData<RecordType>(data: RecordType[], filterStates: NTable.IFilterState<RecordType>[]) {
  return filterStates.reduce((currentData, filterState) => {
    const {
      column: { onFilter, filters },
      filteredKeys
    } = filterState;

    if (onFilter && filteredKeys && filteredKeys.length) {
      return currentData.filter((record) =>
        filteredKeys.some((key) => {
          const keys = flattenKeys(filters);
          const keyIndex = keys.findIndex((k) => String(k) === String(key));
          const realKey = keyIndex !== -1 ? keys[keyIndex] : key;
          return onFilter(realKey, record);
        })
      );
    }
    return currentData;
  }, data);
}

function injectFilter<RecordType>(
  columns: NTable.NColumn.TColumns,
  filterStates: NTable.IFilterState<RecordType>[],
  triggerFilter: (filterState: NTable.IFilterState<RecordType>) => void,
  getPopupContainer: NTable.NCustomize.TGetPopupContainer | undefined,
  locale: NTable.ILocale,
  pos?: string
): NTable.NColumn.TColumns<RecordType> {
  return columns.map((column, index) => {
    const columnPos = getColumnPos(index, pos);
    const { filterMultiple = true, filterMode, filterSearch } = column;

    let newColumn: NTable.NColumn.TColumns<RecordType>[number] = column;
    const hasFilterDropdown = column.filterDropdown || column?.customFilterDropdown;

    if (newColumn.filters || hasFilterDropdown) {
      const columnKey = getColumnKey(newColumn, columnPos);
      const filterState = filterStates.find(({ key }) => key === columnKey);

      newColumn = {
        ...newColumn,
        title: (renderProps: NTable.NColumn.ITitleProps<RecordType>) =>
          h(
            FilterDropdown,
            {
              column: newColumn,
              columnKey,
              filterState,
              filterMultiple,
              filterMode,
              filterSearch,
              triggerFilter,
              locale,
              getPopupContainer
            },
            {
              default: () => renderColumnTitle(column.title, renderProps)
            }
          )
      };
    }

    if ('children' in newColumn) {
      newColumn = {
        ...newColumn,
        children: injectFilter(newColumn.children, filterStates, triggerFilter, getPopupContainer, locale, columnPos)
      };
    }

    return newColumn;
  });
}

function collectFilterStates<RecordType>(
  columns: NTable.NColumn.TColumns<RecordType>,
  init: boolean,
  pos?: string
): NTable.IFilterState<RecordType>[] {
  let filterStates: NTable.IFilterState<RecordType>[] = [];

  (columns || []).forEach((column, index) => {
    const columnPos = getColumnPos(index, pos);
    const hasFilterDropdown = column.filterDropdown || column?.customFilterDropdown;

    if (column.filters || hasFilterDropdown || 'onFilter' in column) {
      if ('filteredValue' in column) {
        let filteredValues = column.filteredValue;

        if (!hasFilterDropdown) {
          filteredValues = filteredValues?.map(String) ?? filteredValues;
        }

        filterStates.push({
          column,
          key: getColumnKey(column, columnPos),
          filteredKeys: filteredValues as NTable.TFilterKey,
          forceFiltered: column.filtered
        });
      } else {
        filterStates.push({
          column,
          key: getColumnKey(column, columnPos),
          filteredKeys: (init && column.defaultFilteredValue
            ? column.defaultFilteredValue!
            : undefined) as NTable.TFilterKey,
          forceFiltered: column.filtered
        });
      }
    }

    if ('children' in column) {
      filterStates = [...filterStates, ...collectFilterStates(column.children, init, columnPos)];
    }
  });

  return filterStates;
}

function generateFilterInfo<RecordType>(filterStates: NTable.IFilterState<RecordType>[]) {
  const currentFilters: Record<string, NTable.TFilterValue | null> = {};

  filterStates.forEach(({ key, filteredKeys, column }) => {
    const hasFilterDropdown = column.filterDropdown || column?.customFilterDropdown;
    const { filters } = column;

    if (hasFilterDropdown) {
      currentFilters[key] = filteredKeys || null;
    } else if (Array.isArray(filteredKeys)) {
      const keys = flattenKeys(filters);
      currentFilters[key] = keys.filter((originKey) => filteredKeys.includes(String(originKey)));
    } else {
      currentFilters[key] = null;
    }
  });

  return currentFilters;
}
